import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { v4 as getUuid } from "uuid";
import { useUser } from "../../hooks/useUser";
import {
	useFetchDetailedApplicationsQuery,
	useFetchEngagementsQuery,
	useFetchPromotionsQuery,
} from "../../services/application";
import {
	useFetchClcApplicationsQuery,
	useInitClcApplicationMutation,
	useFetchClcPossibleOrganisationsQuery,
} from "../../services/clc-application";
import type { ActiveEngagement, InactiveEngagement } from "../../types/application";

export class InvalidApplicantError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = "InvalidApplicantError";
	}
}

export class CreditCheckFailedError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = "CreditCheckFailedErrors";
	}
}

export const useDashboard = () => {
	const navigate = useNavigate();
	const { getLoggedInUser, isLoading: isLoadingUser } = useUser();

	const [initClcApplicationMutation, { isLoading: isInitingClcApplication }] =
		useInitClcApplicationMutation();

	const { data: engagements = [], isLoading: isLoadingEngagements } = useFetchEngagementsQuery();

	const { data: promotions = [], isLoading: isLoadingPromotions } = useFetchPromotionsQuery();

	const { data: applications = [], isLoading: isLoadingApplications } =
		useFetchDetailedApplicationsQuery();

	const { data: clcApplications = [], isLoading: isLoadingClcApplications } =
		useFetchClcApplicationsQuery();

	const [nonVerifiedApplication, setNonVerifiedApplication] = useState<string | null>(null);

	const initClcApplication = async () => {
		const applicationId = getUuid();
		await initClcApplicationMutation({ applicationId }).unwrap();
		navigate(`/new-company/${applicationId}`);
	};

	const clcApplicationIdAwaitingOrgNr = clcApplications.find(
		(a) => a.clcStatus === "AwaitingOrgNumber",
	)?.applicationId;

	const { data: possibleClcOrgs = [], isLoading: isLoadingPossibleOrgs } =
		useFetchClcPossibleOrganisationsQuery(clcApplicationIdAwaitingOrgNr ?? skipToken);

	const verifyNewApplication = () => {
		setNonVerifiedApplication(null);
	};

	const activeEngagements = engagements.filter((engagement): engagement is ActiveEngagement =>
		Boolean(engagement.bahId),
	);
	const inactiveEngagements = engagements.filter(
		(engagement): engagement is InactiveEngagement =>
			!engagement.bahId &&
			!clcApplications?.some((a) => a.connectedOrgNumber === engagement.orgNr) &&
			!applications.some(
				(a) => a.orgNr === engagement.orgNr && a.applicationId !== nonVerifiedApplication,
			),
	);

	return {
		getLoggedInUser,
		activeEngagements: activeEngagements || [],
		inactiveEngagements: inactiveEngagements || [],
		applications,
		isLoading:
			isLoadingEngagements ||
			isLoadingApplications ||
			isLoadingUser ||
			isLoadingClcApplications ||
			isLoadingPossibleOrgs ||
			isLoadingPromotions,
		initClcApplication,
		isInitingClcApplication,
		verifyNewApplication,
		nonVerifiedApplication,
		clcApplications,
		setNonVerifiedApplication,
		possibleClcOrgs,
		promotions,
	};
};
